"use client";

import { addHeadingIds } from "@/src/utils/html-parser";
import { Box, Typography } from "@mui/material";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import DynamicPage from "@/src/components/common/dynamic-page/dynamic-page";
import Hierarchy from "@/src/components/common/hierarchy/hierarchy";
import LastUpdate from "@/src/components/common/pages/last-update/last-update";
import Outlines from "@/src/components/common/outlines/outlines";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import Kicker from "@/src/components/common/broadsheet/kicker";
import Panel from "@/src/components/common/broadsheet/panel";
import Prose from "@/src/components/common/broadsheet/prose";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  HEADING_SIZE,
  RAIL,
  SHEET_GUTTER,
  stickyRailSx,
} from "@/src/components/common/broadsheet/tokens";
import type Page from "@/src/_types/Page/page";

export default function PageComponent({
  initialJournalData,
  initialPageData,
  pageSlug,
}: {
  initialJournalData: any;
  initialPageData: any;
  journalTitle: string;
  pageSlug: string;
}) {
  const journal = initialJournalData;
  const page: Page = initialPageData;
  const modifiedContent = addHeadingIds(page?.content);

  const hasChildren =
    Array.isArray(page?.hierarchy) &&
    page.hierarchy.length > 0 &&
    Array.isArray(page.hierarchy[0]?.childes) &&
    page.hierarchy[0].childes.length > 0;

  const summary = page?.summary ? stripHTMLTags(page.summary, ["span"]) : "";

  const hasOutline = /<h[2-6][^>]*\sid=/i.test(modifiedContent);

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            justifyContent: "space-between",
            columnGap: 1,
            rowGap: 0.375,
          }}
        >
          {page?.title ? (
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              {page.title}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flex: "none",
            }}
          >
            {page?.updated_at ? (
              <LastUpdate updatedAt={page.updated_at} />
            ) : null}
            <SocialIcons direction="column" />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: [
              hasChildren ? RAIL : null,
              "1fr",
              hasOutline ? RAIL : null,
            ]
              .filter(Boolean)
              .join(" "),
          },
          columnGap: 2.5,
          rowGap: 1.25,
          alignItems: "start",
          "& > *": { minWidth: 0 },
        }}
      >
        {hasChildren ? (
          <Box
            sx={{
              ...stickyRailSx,
              display: { xs: "none", lg: "block" },
              pt: 1.25,
              pr: 1.25,
              borderRight: "1px solid",
              borderColor: "custom.ruleHairline",
            }}
          >
            <Hierarchy
              hierarchies={page.hierarchy}
              journal={journal}
              pageSlug={pageSlug}
            />
          </Box>
        ) : null}

        <Box component="article" sx={{ pt: { xs: 1, md: 1.25 } }}>
          {summary ? (
            <Panel id="page-summary" sx={{ mb: 1.25 }}>
              <Kicker color="custom.link" sx={{ mb: 0.25 }}>
                Summary
              </Kicker>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_TEXT,
                  fontSize: "0.9375rem",
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                }}
              >
                {summary}
              </Typography>
            </Panel>
          ) : null}

          <Prose>
            <DynamicPage htmlContent={modifiedContent} />
          </Prose>
        </Box>

        {hasOutline ? (
          <Box
            sx={{
              ...stickyRailSx,
              display: { xs: "none", lg: "block" },
              pt: 1.25,
              pl: 1.25,
              borderLeft: "1px solid",
              borderColor: "custom.ruleHairline",
            }}
          >
            <Outlines
              content={modifiedContent}
              hasAbstract={!!summary}
              hasBody={false}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
